import React, { useState } from 'react';
import { useMutation, useQuery } from 'react-query';

import { message } from 'antd';
import styled from 'styled-components';

import { getAICompetitiveIntelligence, regenerateAICompetitiveIntelligence } from 'apis/RecommendationsAPI';
import Button from 'components/lib/Button';
import Dropdown from 'components/lib/Dropdown';
import Modal from 'components/lib/Modal';
import { useAppSelector } from 'store/hook';

import AICIRegenerateSelection from './AICIRegenerateSelection';
import AICIRegenerationStartedModal from './AICIRegenerationStartedModal';
import {
  AICI_REGENERATE_MENU_KEY,
  AICI_REGENERATION_FEATURE,
  AICI_REGENERATION_FEATURE_OPTIONS,
  OVERALL_AI_CI_LABEL,
} from './constants';

const Container = styled.div`
  .icon-wrapper {
    padding: 7px;
  }
`;

const RegenerateModal = styled(Modal)`
  .ant-modal-content {
    display: flex;
    flex-direction: column;
    height: 90vh;
  }

  .ant-modal-header,
  .ant-modal-footer {
    flex-shrink: 0;
  }

  .ant-modal-body {
    flex: 1;
    min-height: 0;
    overflow: hidden;
  }
`;

const Subtitle = styled.div`
  font-family: var(--font-regular);
  font-size: 14px;
  font-weight: 500;
  color: var(--dark-blue);
`;

const StyledButton = styled(Button)`
  min-width: 150px;
  height: 40px;
`;

const AICIRegenerate = () => {
  const { selectedCommunity } = useAppSelector((state) => state.global);
  const { data, refetch } = useQuery({
    queryKey: ['ai-competitive-intelligence', selectedCommunity?.id],
    queryFn: () => getAICompetitiveIntelligence(selectedCommunity?.id ?? -1),
    enabled: Boolean(selectedCommunity?.id),
    refetchOnWindowFocus: false,
  });
  const relatedData = data?.related_data ?? [];
  const [isOpen, setIsOpen] = useState(false);
  const [categoryConfigIds, setCategoryConfigIds] = useState<number[]>([]);
  const [features, setFeatures] = useState<string[]>([]);
  const [regeneratedLabels, setRegeneratedLabels] = useState<string[]>([]);

  const allCategoryConfigIds = relatedData.map((item) => item.config_id);
  const allFeatures = AICI_REGENERATION_FEATURE_OPTIONS.map((option) => option.value as string);
  const selectedCount = categoryConfigIds.length + features.length;

  const mutation = useMutation(regenerateAICompetitiveIntelligence, {
    onSuccess: (_, payload) => {
      setIsOpen(false);
      setRegeneratedLabels([
        ...relatedData
          .filter((item) => payload.category_config_ids.includes(item.config_id))
          .map((item) => item.display_label),
        ...(payload.category_config_ids.length ? [OVERALL_AI_CI_LABEL] : []),
        ...AICI_REGENERATION_FEATURE_OPTIONS.filter((option) => payload[option.value]).map((option) => option.label),
      ]);
    },
    onError: () => {
      message.error('Unable to start AI CI regeneration');
    },
  });

  const open = () => {
    refetch();
    setCategoryConfigIds([]);
    setFeatures([]);
    setIsOpen(true);
  };

  const onCategoriesChange = (values: number[]) => {
    if (!categoryConfigIds.length && values.length) {
      setFeatures(allFeatures);
    }
    setCategoryConfigIds(values);
  };

  const toggleCategory = (configId: number, checked: boolean) =>
    onCategoriesChange(
      checked ? [...categoryConfigIds, configId] : categoryConfigIds.filter((selectedId) => selectedId !== configId)
    );

  const toggleFeature = (feature: string, checked: boolean) =>
    setFeatures(checked ? [...features, feature] : features.filter((selectedFeature) => selectedFeature !== feature));

  const toggleAll = (checked: boolean) => {
    setCategoryConfigIds(checked ? allCategoryConfigIds : []);
    setFeatures(checked ? allFeatures : []);
  };

  const menuItems = [{ key: AICI_REGENERATE_MENU_KEY, label: 'Regenerate AI CI', onClick: open }];

  return (
    <Container>
      <Dropdown trigger={['click']} menu={{ items: menuItems }}>
        <Button className="icon-wrapper" aria-label="More actions" data-test-id="aici-more-actions">
          <img src="/icons/elipsis.svg" alt="More actions" />
        </Button>
      </Dropdown>
      <RegenerateModal
        open={isOpen}
        centered
        destroyOnClose
        width={1160}
        className="compact bare-header"
        data-test-id="aici-regenerate-modal"
        title={
          <div>
            <div>Regenerate AI CI</div>
            <Subtitle>{selectedCommunity?.name}</Subtitle>
          </div>
        }
        onCancel={() => setIsOpen(false)}
        footer={[
          <StyledButton
            key="submit"
            type="primary"
            data-test-id="aici-regenerate-submit"
            disabled={!selectedCount}
            loading={mutation.isLoading}
            onClick={() =>
              mutation.mutate({
                community: selectedCommunity?.id ?? -1,
                category_config_ids: categoryConfigIds,
                action_plans: features.includes(AICI_REGENERATION_FEATURE.ACTION_PLANS),
                sales_talk_tracks: features.includes(AICI_REGENERATION_FEATURE.SALES_TALK_TRACKS),
              })
            }
          >
            Regenerate
          </StyledButton>,
          <StyledButton key="cancel" data-test-id="aici-regenerate-cancel" onClick={() => setIsOpen(false)}>
            Cancel
          </StyledButton>,
        ]}
      >
        <AICIRegenerateSelection
          data={data}
          categoryConfigIds={categoryConfigIds}
          features={features}
          onCategoryToggle={toggleCategory}
          onFeatureToggle={toggleFeature}
          onToggleAll={toggleAll}
        />
      </RegenerateModal>
      <AICIRegenerationStartedModal
        open={Boolean(regeneratedLabels.length)}
        communityName={selectedCommunity?.name}
        regeneratedLabels={regeneratedLabels}
        onClose={() => setRegeneratedLabels([])}
      />
    </Container>
  );
};

export default AICIRegenerate;
